import PageHeaderText from 'components/base/page-header-text';
import React, { useCallback, useEffect, useState } from 'react';
import { ALL } from 'utils/constants';
import { ENUM_RETURNS_STATUS } from 'models/common.model';
import KitchenExcessReturnsFilter from './excess-returns-filter';
import { debounce } from 'utils/functions';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import ReturnsCard from 'components/returns-card';
import { ExcessReturnsType } from 'pages/main-store/excess-returns';
import useDashboardContext from 'contexts/dashboard.context';

export type ExtendedReturnsType = ENUM_RETURNS_STATUS | typeof ALL;
export type FilterType = {
  search: string;
  status: ExtendedReturnsType;
  dateRange: { startDate: Date; endDate: Date };
};

type Ingredient = {
  id: string;
  name: string;
  category: string;
  unit_weight: string;
  available_quantity: number;
  min_quantity: number;
  min_vendor_price: string;
  archived: boolean;
  created_at: string;
  updated_at: string;
  type: string | null;
  active: string | null;
  unit_type: string;
};

type Item = {
  id: string;
  ingredient_id: string;
  return_id: string;
  quantity: number;
  ingredient: Ingredient;
};

export type ReturnsType = {
  id: string;
  cokitchen_id: string;
  status: ENUM_RETURNS_STATUS;
  note: string;
  code: string;
  items: Item[];
  created_at: string;
};

const KitchenExcessReturns = () => {
  const { activeStore } = useDashboardContext();
  const [dataArray, setDataArray] = useState<ExcessReturnsType[]>([]);

  const [filters, setFilters] = useState({
    search: '',
    status: ALL,
    dateRange: {
      startDate: new Date(),
      endDate: new Date()
    }
  } as FilterType);

  const fetchItemsInStock = async (filters: FilterType, current_page?: number) => {
    if (!activeStore?.id) return;
    try {
      const response = await apiService.get(
        `kitchen-store/stores/${activeStore.id}/kitchen-return`,
        { params: { search: filters.search, page: current_page || '1' } }
      );
      if (response) {
        setDataArray(response?.data?.results);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching excess returns'}
          type='error'
        />
      );
    }
  };

  const debounceFetchItemsInStock = useCallback(debounce(fetchItemsInStock, 1000), []);

  useEffect(() => {
    setDataArray([]);
    debounceFetchItemsInStock(filters, 1);
  }, [filters]);

  return (
    <div>
      <PageHeaderText
        title='Excess returns'
        description='Track excess items returned by the kitchen store'></PageHeaderText>
      <KitchenExcessReturnsFilter filters={filters} setFilters={setFilters} />
      <div className='mt-10 gap-7 grid grid-cols-3 max-[1250px]:grid-cols-2 max-[768px]:grid-cols-1'>
        {dataArray.map((p, i) => (
          <ReturnsCard key={i} returns={p} />
        ))}
      </div>
    </div>
  );
};

export default KitchenExcessReturns;
